import { Box, Button, ButtonProps, Flex, Image, Text } from '@chakra-ui/react';
import bgBrandModelImage from 'assets/background/brand_modal.png';
import { ReactComponent as NewTabIcon } from 'assets/icons/new-tab.svg';
import { BRAND_REDIRECT_LINK } from 'constants/links';
import { IMAGE_CREATED } from 'constants/mixpanel';
import { Mixpanel } from 'libs/third-party/Mixpanel/mixpanel';
import { getInitialWidgetPosition } from 'modules/Editor/Editor.helpers';
import { BrandOptionDropdown } from 'modules/Editor/components/BrandOptionDropdown';
import { useAppSelector } from 'modules/Editor/store';
import { selectBrandIdx } from 'modules/Editor/store/editorSettingsSelector';
import { useBrand, useUser } from 'modules/User';
import { ReactElement, memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { generateDefaultData as generateImageData } from 'widgets/ImageWidget/ImageWidget.helpers';
import { NewWidget } from 'widgets/Widget.types';

import { Cell, CellWithBadge, InfiniteGrid } from '../InfiniteGrid';
import { THUMBNAIL_HEIGHT, THUMBNAIL_WIDTH } from '../InfiniteGrid/InfiniteGrid.config';
import { serializeLogoData } from './BrandKitMenu.helper';

export interface BrandKitMenuProps {
  onItemClick: (widget: NewWidget | NewWidget[]) => void;
}

export const BrandKitMenu = memo(({ onItemClick }: BrandKitMenuProps) => {
  const { isFreeUser, isPremiumUser } = useUser();
  const { data: brandData } = useBrand();
  const brandIdx = useAppSelector(selectBrandIdx);
  const brandLogos = brandData?.[brandIdx]?.logos ?? [];

  const { brandConfig = { editable: false } } = useBrand({
    brandId: brandData?.[brandIdx]?.id,
  });

  const { t } = useTranslation('editor_widget_menu', { useSuspense: false });

  const serializedLogoData = serializeLogoData({ data: brandLogos });

  // Free and Premium users should always not have the brand dropdown. other user types depends on the number of brands.
  const showBrandKitDropdown = !isFreeUser && !isPremiumUser;

  // Free and Premium users should not be able to use but see the logo's with business badge labeled.
  const hasLogoPermission = !isFreeUser && !isPremiumUser;

  // Free and Premium has the permission to edit their brand because they are the owner.
  // Enterprise/Business admin users should have the permission to edit their brand. However, Members and Guests are not.
  // Right now, E1 backend handle the permission check.
  const hasBrandEditPermission = isFreeUser || isPremiumUser || brandConfig.editable;

  // Determine if showing the logo view. if no logos set on E1, use the NoLogoView to gate them
  const showLogos = !!serializedLogoData.length;

  const onLogoClick = useCallback(
    (item: unknown, _: number | undefined, imageDom: HTMLImageElement | undefined | null) =>
      new Promise((resolve) => {
        const typedItem = item as typeof serializedLogoData[number];

        const { naturalHeight = THUMBNAIL_HEIGHT, naturalWidth = THUMBNAIL_WIDTH } = imageDom || {};

        const { top, left, infographWidth, infographHeight } = getInitialWidgetPosition();
        const widget = generateImageData(
          typedItem.previewURL, // image url
          { width: naturalWidth, height: naturalHeight }, // image size
          { width: infographWidth, height: infographHeight }, // infograph size
          top,
          left,
        );

        onItemClick(widget);

        Mixpanel.track(IMAGE_CREATED.eventName, {
          from: IMAGE_CREATED.from.Brand,
        });
        resolve(widget);
      }),
    [onItemClick],
  );

  return (
    <Flex direction='column' h='full' padding={2} paddingTop={4}>
      <Flex justifyContent='space-between' alignItems='center' paddingLeft='2' paddingBottom='2'>
        <Text color='black' fontWeight='medium' fontSize='sm' maxHeight='fit-content' marginY='auto'>
          Brand Kit
        </Text>
        {showBrandKitDropdown && <BrandOptionDropdown w={56} triggerProps={{ h: 8 }} />}
      </Flex>
      <Box h={0} flexGrow={1}>
        {!showLogos && (
          <NoLogoView
            buttonLabel={t('sideMenu.brandKit.uploadLogosToBrandKit')}
            hasBrandEditPermission={hasBrandEditPermission}
          />
        )}
        {showLogos && (
          <InfiniteGrid
            data={serializedLogoData}
            numberOfItems={serializedLogoData.length}
            onItemClick={onLogoClick}
            cell={hasLogoPermission ? Cell : CellWithBadge}
          />
        )}
      </Box>
      {showLogos && hasBrandEditPermission && <EditBrandKitButton />}
    </Flex>
  );
});
BrandKitMenu.displayName = 'BrandTab';

export const EditBrandKitButton = (props: Omit<ButtonProps, 'ref'>): ReactElement => {
  const { t } = useTranslation('editor_widget_menu', { useSuspense: false });
  return (
    <Button
      as='a'
      href={BRAND_REDIRECT_LINK}
      target='_blank'
      rel='noopener noreferrer'
      leftIcon={<NewTabIcon />}
      size='md'
      variant='outline'
      marginX={props.marginX ?? '4'}
      marginY={props.marginY ?? '3'}
      height={props.height ?? '10'}
      {...props}
    >
      {t('sideMenu.stylesMenu.editBrandKit')}
    </Button>
  );
};

interface NoLogoViewProps {
  buttonLabel: string;
  hasBrandEditPermission: boolean;
}

export const NoLogoView = ({ buttonLabel, hasBrandEditPermission }: NoLogoViewProps) => {
  return (
    <Flex
      direction='column'
      h='full'
      alignItems='center'
      justifyContent='center'
      paddingX={8}
      data-testid='brand-no-logo-view'
    >
      <Image src={bgBrandModelImage} alt='Brand Model' />
      <Text fontSize='md' fontWeight='semibold' marginTop={4}>
        Look professional with Brand Kit
      </Text>
      <Text fontSize='sm' marginTop={4}>
        Brand consistency made easy.
      </Text>
      <Text fontSize='sm' textAlign='center'>
        Save time with logo, font, and color presets.
      </Text>
      {hasBrandEditPermission && (
        <Button
          as='a'
          href={BRAND_REDIRECT_LINK}
          target='_blank'
          rel='noopener noreferrer'
          leftIcon={<NewTabIcon />}
          size='md'
          variant='upgrade'
          marginTop={4}
        >
          {buttonLabel}
        </Button>
      )}
      {!hasBrandEditPermission && (
        <Text fontSize='sm' fontWeight='semibold' textAlign='center' marginTop={4}>
          Ask your administrator to setup your team's Brand Kit{' '}
        </Text>
      )}
    </Flex>
  );
};
